import React from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { ArrowRight } from "@phosphor-icons/react";
import { Domain } from "Domain";

const headCellSx = {
  fontSize: "12px",
  fontWeight: 800,
  color: "#64748b",
  letterSpacing: 0,
  border: 0,
  background: "#fbfdff",
  py: 1,
};

export default function CustomTable1({
  tableHead,
  tableData,
  setEdit,
  loading,
  noAction,
  Images,
  disablePagination,
}) {
  const allRows = tableData || [];
  const headers = tableHead || [];

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const rows = React.useMemo(() => {
    if (disablePagination) return allRows;
    const start = page * rowsPerPage;
    return allRows.slice(start, start + rowsPerPage);
  }, [allRows, page, rowsPerPage, disablePagination]);

  React.useEffect(() => {
    setPage(0);
  }, [allRows]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <TableContainer sx={{ borderRadius: "8px" }}>
        <Table size="small">
          {headers.length > 0 && (
            <TableHead>
              <TableRow>
                {headers.map((head) => (
                  <TableCell key={head} sx={headCellSx}>
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {loading ? (
              Array.from({ length: 4 }).map((_, index) => (
                <TableRow key={index}>
                  {headers.map((head) => (
                    <TableCell key={head} sx={{ py: 1.1 }}>
                      <Skeleton width="70%" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : allRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={Math.max(headers.length, 1)}
                  align="center"
                  sx={{ border: 0, py: 3 }}
                >
                  <Typography sx={{ color: "#94a3b8", fontSize: "14px" }}>
                    No records found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, rowIndex) => (
                <TableRow
                  key={row.Id || row.Name || rowIndex}
                  hover
                  sx={{
                    "&:last-child td": { border: 0 },
                    "&:hover": { background: "#f8fbff" },
                  }}
                >
                  {Images && row.Image && (
                    <TableCell
                      sx={{ borderBottom: "1px solid #eef3f9", py: 0.85 }}
                    >
                      <Box
                        component="img"
                        src={Domain + row.Image}
                        alt=""
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: "8px",
                          objectFit: "cover",
                        }}
                      />
                    </TableCell>
                  )}
                  {Object.values(row).map((value, cellIndex) => (
                    <TableCell
                      key={`${rowIndex}-${cellIndex}`}
                      sx={{
                        borderBottom: "1px solid #eef3f9",
                        py: 0.9,
                        color: "#1e293b",
                        fontSize: "13px",
                        fontWeight: 700,
                      }}
                    >
                      {value}
                    </TableCell>
                  ))}
                  {!noAction && (
                    <TableCell
                      align="right"
                      sx={{ borderBottom: "1px solid #eef3f9", py: 0.6 }}
                    >
                      <Tooltip title="Open billing report">
                        <IconButton
                          size="small"
                          onClick={() => setEdit(row.Name)}
                          sx={{
                            color: "#2f72f2",
                            background: "#e8f0fe",
                            borderRadius: "8px",
                            "&:hover": { background: "#d8e7ff" },
                          }}
                        >
                          <ArrowRight size={18} weight="duotone" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!disablePagination && allRows.length > 0 && (
        <TablePagination
          component="div"
          count={allRows.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      )}
    </>
  );
}

CustomTable1.defaultProps = {
  tableHeaderColor: "gray",
  tableData: [],
  loading: false,
  noAction: false,
  Images: false,
  disablePagination: false,
};

CustomTable1.propTypes = {
  tableHeaderColor: PropTypes.oneOf([
    "warning",
    "primary",
    "danger",
    "success",
    "info",
    "rose",
    "gray",
  ]),
  tableHead: PropTypes.arrayOf(PropTypes.string),
  tableData: PropTypes.array,
  setEdit: PropTypes.func,
  loading: PropTypes.bool,
  noAction: PropTypes.bool,
  Images: PropTypes.bool,
  disablePagination: PropTypes.bool,
};
